import * as React from 'react'
import { useController, useFormContext } from 'react-hook-form'

import { Description as FieldDescription } from '~/components/field-wrapper/FieldDescription'
import { FieldElementWrapperProps } from '~/components/field-wrapper/FieldWrapper'
import { useFieldError } from '~/components/form/useFieldError'
import { InlineMessage } from '~/components/inline-message/InlineMessage'
import { Label } from '~/components/label/Label'
import { RadioButtonGroup } from '~/components/radio-button/RadioButtonGroup'
import { styled } from '~/styled'

import { RadioField } from './RadioField'

const Fieldset = styled('fieldset', {
  base: ['border-0', 'p-0', 'm-0', 'min-w-0']
})

type RadioButtonFieldProps = React.ComponentProps<typeof RadioButtonGroup> &
  FieldElementWrapperProps

const RadioButtonFieldComponent = ({
  children,
  className,
  direction = 'column',
  defaultValue,
  value,
  description,
  label,
  name,
  validation,
  onValueChange,
  ...remainingProps
}: RadioButtonFieldProps) => {
  const { control } = useFormContext()
  const {
    field: { ref, onChange, value: innerValue }
  } = useController({
    name,
    control,
    rules: validation,
    defaultValue
  })
  const { error } = useFieldError(name)

  React.useEffect(() => {
    // Update the react-hook-form inner value to match what is passed in.
    if (typeof value !== 'undefined') onChange(value)
  }, [value])

  return (
    <Fieldset className={className}>
      <Label
        as="legend"
        required={Boolean(validation?.required)}
        className="mb-3 p-0"
      >
        {label}
      </Label>
      {description && (
        <FieldDescription className="mb-3">{description}</FieldDescription>
      )}

      <RadioButtonGroup
        ref={ref}
        direction={direction}
        defaultValue={defaultValue}
        onValueChange={(newValue) => {
          onChange(newValue)
          onValueChange?.(newValue)
        }}
        value={innerValue}
        {...remainingProps}
      >
        {children}
      </RadioButtonGroup>

      {error && <InlineMessage className="mt-2">{error}</InlineMessage>}
    </Fieldset>
  )
}

export const RadioButtonField = Object.assign(RadioButtonFieldComponent, {
  Item: RadioField
})

RadioButtonFieldComponent.displayName = 'RadioButtonField'
